// src/components/PublicListPage.jsx
// Public curated list page — /list/:id
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../context/UserContext";
import { STATUS_EMOJI, STATUS_COLOR, FRONTEND_URL } from "../constants";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54", primaryDim: "#595149",
  bg: "#faf9f6", surfaceLow: "#f4f4f0",
  container: "#edeeea", containerLowest: "#ffffff",
  onSurface: "#2f3430", outlineVariant: "#8a8e8a",
  primaryContainer: "#ede0d5",
};

function ensureGoogleMaps(cb) {
  if (window.google?.maps?.Map) { cb(); return; }
  if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
    const check = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(check); cb(); }
    }, 100);
    return;
  }
  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=marker&v=weekly&loading=async&language=${localStorage.getItem("lang") || "en"}`;
  script.async = true;
  script.onload = () => {
    if (window.google?.maps?.Map) { cb(); return; }
    const check = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(check); cb(); }
    }, 100);
  };
  document.head.appendChild(script);
}

export default function PublicListPage({ listId }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    axios.get(`${API_BASE}/lists/${listId}`)
      .then((res) => setList(res.data))
      .catch((e) => setError(e.response?.data?.detail || "List not found"))
      .finally(() => setLoading(false));
  }, [listId]);

  // Load Google Maps SDK
  useEffect(() => {
    if (!list?.places?.length || !mapContainerRef.current) return;
    ensureGoogleMaps(() => setMapReady(true));
  }, [list]);

  // Init map
  useEffect(() => {
    if (!mapReady || !list?.places?.length || !mapContainerRef.current || mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    list.places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: bounds.getCenter(), zoom: 13, minZoom: 2,
      restriction: { latLngBounds: { north: 85, south: -85, west: -180, east: 180 }, strictBounds: false },
      zoomControl: false, mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      gestureHandling: "greedy",
      mapId: process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID",
    });
    map.fitBounds(bounds, { top: 60, bottom: isMobile ? 180 : 60, left: 40, right: 40 });
    mapRef.current = map;

    list.places.forEach((p, i) => {
      const sc = STATUS_COLOR[p.status];
      const content = document.createElement("div");
      content.innerHTML = `<div style="
        padding:3px 8px;border-radius:16px;
        background:${sc?.bg || C.surfaceLow};color:${sc?.color || C.onSurface};
        font-family:${FL};font-size:10px;font-weight:700;
        white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.12);
        border:2px solid white;
      ">${i + 1}. ${p.name}</div>`;
      new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: p.lat, lng: p.lng }, map, content,
      });
    });

    return () => { mapRef.current = null; };
  }, [mapReady, list, isMobile]);

  const shareList = () => {
    const url = `${FRONTEND_URL}/list/${listId}`;
    if (navigator.share) {
      navigator.share({ url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const copyLink = () => {
    const url = `${FRONTEND_URL}/list/${listId}`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, flexDirection: "column", gap: 10 }}>
        <img src="/logo.svg" alt="" style={{ width: 48, height: 48 }} />
        <p style={{ fontFamily: FL, fontSize: 12, color: C.outlineVariant }}>Loading...</p>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, flexDirection: "column", gap: 12 }}>
        <img src="/logo.svg" alt="" style={{ width: 48, height: 48 }} />
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.outlineVariant }}>{error || "List not found"}</p>
        <a href="/" style={{ fontFamily: FL, fontSize: 12, color: C.primary, textDecoration: "none", padding: "8px 20px", background: C.primaryContainer, borderRadius: 8, fontWeight: 600 }}>
          Start your own
        </a>
      </div>
    );
  }

  const places = list.places || [];

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: C.bg }}>
      {/* Header */}
      <header style={{
        padding: isMobile ? "14px 16px" : "16px 32px",
        background: "rgba(250,249,246,0.9)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${C.container}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        zIndex: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontFamily: FH, fontSize: isMobile ? 18 : 22, color: C.onSurface }}>
            {list.title}
          </h1>
          <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>
            {list.owner_nickname} &middot; {places.length} places
            {list.description ? ` · ${list.description}` : ""}
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={copyLink} style={{
            fontFamily: FL, fontSize: 10, fontWeight: 600, color: C.primary,
            background: C.container, border: "none", padding: "6px 10px",
            borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 3,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{copied ? "check" : "link"}</span>
            {copied ? "Copied!" : "Copy"}
          </button>
          {navigator.share && (
            <button onClick={shareList} style={{
              fontFamily: FL, fontSize: 10, fontWeight: 600, color: "#fff6ef",
              background: C.primary, border: "none", padding: "6px 12px",
              borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 3,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>share</span>
              Share
            </button>
          )}
          <a href="/" style={{
            fontFamily: FL, fontSize: isMobile ? 10 : 11, fontWeight: 700, color: "#fff6ef",
            padding: isMobile ? "6px 12px" : "8px 18px", background: C.primaryDim, borderRadius: 8, textDecoration: "none",
            whiteSpace: "nowrap", display: "flex", alignItems: "center",
          }}>
            Start your own
          </a>
        </div>
      </header>

      {/* Map + side list */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div ref={mapContainerRef} style={{ flex: 1, minHeight: 0 }} />

        {/* Desktop side list */}
        {!isMobile && places.length > 0 && (
          <div style={{
            width: 340, overflowY: "auto", background: C.bg,
            borderLeft: `1px solid ${C.container}`, padding: 16,
          }}>
            <p style={{ margin: "0 0 12px", fontFamily: FL, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant }}>
              {places.length} places in this list
            </p>
            {places.map((p, i) => (
              <PlaceRow key={p.id} place={p} index={i} mapRef={mapRef} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {isMobile && places.length > 0 && (
        <MobileBottomSheet places={places} title={list.title} mapRef={mapRef} />
      )}
    </div>
  );
}

function PlaceRow({ place: p, index: i, mapRef }) {
  const sc = STATUS_COLOR[p.status];
  return (
    <div
      onClick={() => {
        if (mapRef.current) {
          mapRef.current.panTo({ lat: p.lat, lng: p.lng });
          mapRef.current.setZoom(16);
        }
      }}
      style={{ padding: "12px 10px", borderBottom: `1px solid ${C.container}`, cursor: "pointer", transition: "background 0.1s" }}
      onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          fontFamily: FL, fontSize: 11, fontWeight: 700, color: C.primary,
          width: 22, textAlign: "center",
        }}>{i + 1}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontFamily: FH, fontSize: 14, color: C.onSurface }}>{p.name}</p>
          {p.address && <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.address}</p>}
        </div>
        <span style={{
          fontFamily: FL, fontSize: 9, fontWeight: 600, padding: "2px 6px",
          borderRadius: 4, background: sc?.bg, color: sc?.color,
        }}>{STATUS_EMOJI[p.status]}</span>
      </div>
      {p.memo && (
        <p style={{ margin: "4px 0 0 30px", fontFamily: FH, fontStyle: "italic", fontSize: 11, color: "#78716c" }}>"{p.memo}"</p>
      )}
    </div>
  );
}

function MobileBottomSheet({ places, title, mapRef }) {
  const PEEK = 160;
  const FULL_VH = 65;
  const [mode, setMode] = useState("peek");
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const startH = useRef(0);
  const dragging = useRef(false);
  const getMaxH = () => window.innerHeight * (FULL_VH / 100);

  const onTouchStart = useCallback((e) => {
    dragging.current = true;
    startY.current = e.touches[0].clientY;
    startH.current = sheetRef.current?.offsetHeight || PEEK;
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!dragging.current || !sheetRef.current) return;
    const dy = startY.current - e.touches[0].clientY;
    sheetRef.current.style.height = Math.max(PEEK, Math.min(getMaxH(), startH.current + dy)) + "px";
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!dragging.current || !sheetRef.current) return;
    dragging.current = false;
    sheetRef.current.style.transition = "height 0.3s ease";
    const mid = (PEEK + getMaxH()) / 2;
    if (sheetRef.current.offsetHeight > mid) {
      sheetRef.current.style.height = getMaxH() + "px";
      setMode("full");
    } else {
      sheetRef.current.style.height = PEEK + "px";
      setMode("peek");
    }
  }, []);

  const toggleMode = () => {
    const next = mode === "peek" ? "full" : "peek";
    setMode(next);
    if (sheetRef.current) {
      sheetRef.current.style.transition = "height 0.3s ease";
      sheetRef.current.style.height = (next === "full" ? getMaxH() : PEEK) + "px";
    }
  };

  return (
    <div
      ref={sheetRef}
      style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: PEEK, background: C.containerLowest,
        borderTopLeftRadius: 16, borderTopRightRadius: 16,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
        transition: "height 0.3s ease",
        display: "flex", flexDirection: "column", zIndex: 20,
      }}
    >
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={toggleMode}
        style={{ padding: "12px 16px", cursor: "pointer", textAlign: "center", flexShrink: 0 }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.container, margin: "0 auto 10px" }} />
        <p style={{ margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 700, color: C.onSurface }}>
          {title} &mdash; {places.length} places
        </p>
      </div>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {places.map((p, i) => (
          <PlaceRow key={p.id} place={p} index={i} mapRef={mapRef} />
        ))}
      </div>
    </div>
  );
}
